<script setup lang="ts">
import {computed} from 'vue';
import NamaIcon from '@/components/icons/NamaIcon.vue';
import ChatUtils from '@/utils/common/ChatUtils.ts';
import ChatPresenter from '@/utils/presenters/ChatPresenter.ts';
import {ChatMetadata} from '@/utils/presenters/interfaces.ts';
import ChatMessageSearchResult = namaTypes.ChatMessageSearchResult;

const props = defineProps<{
  chat: ChatMetadata;
  isActive: boolean;
  currentUserId: string;
  presenter: ChatPresenter;
  snippet?: ChatMessageSearchResult;
  searchQuery?: string;
}>();

defineEmits<{
  click: [];
}>();

function getDirectChatOtherUserId(): string {
  if (props.chat.chatConversationType !== 'Direct' || !props.chat.participants) return '';
  const other = props.chat.participants.find(p => p.participant?.id !== props.currentUserId);
  return other?.participant?.id ?? '';
}

const previewText = computed(() => {
  if (props.snippet)
    return (props.snippet.senderName ? props.snippet.senderName.split(" ")[0] + ': ' : '') + props.snippet.messageContent;
  return props.chat.lastMessagePreview ?? '';
});

function highlightMatch(text: string): string {
  const query = props.searchQuery?.trim();
  if (!query || !text) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  return text.replace(regex, '<mark class="tw-bg-orange-500 dark:tw-bg-orange-300 tw-rounded-sm">$1</mark>');
}
</script>

<template>
  <q-item clickable :active="true" @click="$emit('click')"
          :class="{'tw-rounded-md tw-m-2 tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark tw-text-namaFontColor-input-dark is-current-item': isActive}">
    <q-item-section avatar>
      <div class="tw-relative tw-inline-block">
        <q-avatar color="primary">
          <div v-if="chat.chatConversationType === 'Direct'"
               class="tw-size-fit tw-rounded-full tw-content-center tw-border-namaBackground-deep-light dark:tw-border-namaBackground-deep-dark tw-border-[2px] tw-absolute tw-bottom-0 tw-end-0">
            <div class="tw-rounded-full !tw-size-[8px] !tw-text-[8px]"
                 :class="[presenter.isUserOnline(getDirectChatOtherUserId()) ? 'tw-bg-green-500' : 'tw-bg-gray-500']"/>
          </div>
          <img v-if="chat.chatConversationType === 'Direct'" :src="chat.imageSrc" alt="">
          <NamaIcon v-else defaultIcon="group" class="active-white"/>
        </q-avatar>
      </div>
    </q-item-section>
    <q-item-section>
      <div class="tw-flex tw-flex-row tw-w-full">
        <span v-if="searchQuery" v-html="highlightMatch(chat.chatTitle ?? '')" class="tw-line-clamp-1 tw-w-[80%] tw-max-w-[80%]"/>
        <span v-else v-text="chat.chatTitle" class="tw-line-clamp-1 tw-w-[80%] tw-max-w-[80%]"/>
        <span v-text="ChatUtils.getLastMessageDateLabel(snippet?.sentAt ?? chat.lastMessageAt ?? chat.createdAt)"
              class="tw-text-xs tw-text-gray-500 tw-w-fit"/>
      </div>
      <div class="tw-flex tw-flex-row tw-w-full tw-justify-between">
        <span class="tw-flex tw-items-center tw-gap-1 tw-line-clamp-1 tw-w-[85%] tw-max-w-[85%]"
              :class="[snippet ? 'tw-text-primary-light dark:tw-text-primary-dark' : (chat.unreadMessagesCount ?? 0) > 0 ? 'tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark' : 'tw-text-gray-500']">
          <q-icon v-if="!snippet && ChatUtils.getMessagePreviewIcon(chat.lastMessageType)"
                  :name="ChatUtils.getMessagePreviewIcon(chat.lastMessageType)" size="xs"/>
          <NamaIcon v-if="snippet" defaultIcon="search" class="!tw-text-xs tw-opacity-70 tw-size-3"/>
          <span v-if="searchQuery" v-html="highlightMatch(previewText)" class="tw-line-clamp-1 tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark "/>
          <span v-else v-text="previewText" class="tw-line-clamp-1"/>
        </span>
        <span v-if="(chat.unreadMessagesCount ?? 0) > 0" v-text="chat.unreadMessagesCount"
              class="tw-text-xs tw-w-5 tw-h-5 tw-font-semibold tw-text-center tw-text-namaFontColor-input-dark
                     tw-bg-primary-light dark:tw-bg-primary-dark tw-rounded-full"/>
      </div>
    </q-item-section>
  </q-item>
  <q-separator/>
</template>
